/*
| Settings (Nastavení) — generic list + modal CRUD for the planning frontend.
| Config-driven so each section (workplaces, shift types, users, …) supplies its
| own endpoints + field set via a <script data-settings-config> JSON blob, while
| the list rows are server-rendered (works without JS, soft-nav friendly). The
| modal form is built from the field definitions; on save/delete we reload the
| section so the server stays the single source of truth.
*/
import { screenSignal } from './screenScope';
import { enhanceSelects } from './selects';
import { toast } from './toast';

const CHECK_SVG = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.5 8.5l3 3 6-7"/></svg>';

type Field = {
    key: string;
    label: string;
    type: 'text' | 'number' | 'checkbox' | 'color' | 'select';
    required?: boolean;
    readonlyOnEdit?: boolean;
    createOnly?: boolean;
    editOnly?: boolean;
    options?: Array<{ value: string; label: string }>;
};

type Config = {
    storeUrl: string;
    itemBase: string;
    impact?: boolean;
    fields: Field[];
    palette: string[];
    labels: Record<string, string>;
};

const esc = (v: unknown): string => String(v ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
));
const axios = (): any => (window as any).axios;

export function initSettings(): void {
    const configEl = document.querySelector<HTMLElement>('[data-settings-config]');
    const modal = document.querySelector<HTMLElement>('[data-settings-modal]');
    if (!configEl || !modal) {
        return;
    }
    const cfg = JSON.parse(configEl.textContent || '{}') as Config;

    const titleEl = modal.querySelector<HTMLElement>('[data-settings-title]')!;
    const fieldsEl = modal.querySelector<HTMLElement>('[data-settings-fields]')!;
    const errorEl = modal.querySelector<HTMLElement>('[data-settings-error]')!;
    const form = modal.querySelector<HTMLFormElement>('[data-settings-form]')!;

    let editId: number | null = null;

    const close = (): void => { modal.hidden = true; };
    const open = (): void => { modal.hidden = false; setTimeout(() => fieldsEl.querySelector<HTMLElement>('input,select')?.focus(), 30); };

    modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
    modal.querySelectorAll('[data-settings-cancel]').forEach((b) => b.addEventListener('click', close));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !modal.hidden) close(); }, { signal: screenSignal() });

    function fieldHtml(f: Field, value: unknown, editing: boolean): string {
        const id = `set-f-${f.key}`;
        const ro = editing && f.readonlyOnEdit ? 'readonly' : '';
        if (f.type === 'checkbox') {
            const cbDisabled = editing && f.readonlyOnEdit ? 'disabled' : '';
            return `<label class="pl-check"><input type="checkbox" id="${id}" name="${f.key}" ${value ? 'checked' : ''} ${cbDisabled}><span class="pl-check__box">${CHECK_SVG}</span><span class="pl-check__label">${esc(f.label)}</span></label>`;
        }
        if (f.type === 'color') {
            const swatches = cfg.palette.map((c) => `<label class="pl-swatch bg-${esc(c)}-300" title="${esc(c)}"><input type="radio" name="${f.key}" value="${esc(c)}" ${value === c ? 'checked' : ''}></label>`).join('');
            return `<div class="pl-prof-field"><span class="pl-label">${esc(f.label)}</span><div class="pl-color-swatches" role="radiogroup" aria-label="${esc(f.label)}">${swatches}</div></div>`;
        }
        if (f.type === 'select') {
            const opts = (f.options ?? []).map((o) => `<option value="${esc(o.value)}" ${String(value) === o.value ? 'selected' : ''}>${esc(o.label)}</option>`).join('');
            return `<div class="pl-prof-field"><label class="pl-label" for="${id}">${esc(f.label)}</label><select class="pl-input pl-select" id="${id}" name="${f.key}" data-combo>${opts}</select></div>`;
        }
        const type = f.type === 'number' ? 'number' : 'text';
        return `<div class="pl-prof-field"><label class="pl-label" for="${id}">${esc(f.label)}${f.required ? ' *' : ''}</label><input class="pl-input" type="${type}" id="${id}" name="${f.key}" value="${esc(value ?? '')}" ${f.required ? 'required' : ''} ${ro}></div>`;
    }

    function openModal(row: Record<string, unknown> | null): void {
        editId = row ? Number(row.id) : null;
        titleEl.textContent = row ? cfg.labels.edit : cfg.labels.create;
        errorEl.hidden = true;
        errorEl.textContent = '';
        const visible = cfg.fields.filter((f) => (row ? !f.createOnly : !f.editOnly));
        fieldsEl.innerHTML = visible.map((f) => fieldHtml(f, row ? row[f.key] : (f.type === 'checkbox' ? true : ''), row !== null)).join('');
        enhanceSelects(fieldsEl);
        open();
    }

    function collect(): Record<string, unknown> {
        const out: Record<string, unknown> = {};
        cfg.fields.forEach((f) => {
            const present = fieldsEl.querySelector<HTMLInputElement>(`[name="${f.key}"]`);
            if (!present) {
                return; // field not shown in this mode (create-only / edit-only)
            }
            if (f.type === 'checkbox') {
                out[f.key] = present.checked;
            } else {
                const el = fieldsEl.querySelector<HTMLInputElement>(`[name="${f.key}"]:checked, [name="${f.key}"]:not([type=radio]):not([type=checkbox])`);
                out[f.key] = el?.value ?? '';
            }
        });
        return out;
    }

    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const save = form.querySelector<HTMLButtonElement>('[data-settings-save]')!;
        save.disabled = true;
        try {
            const payload = collect();
            if (editId === null) {
                await axios().post(cfg.storeUrl, payload);
            } else {
                await axios().put(`${cfg.itemBase}/${editId}`, payload);
            }
            location.reload();
        } catch (err: any) {
            save.disabled = false;
            const bag = err?.response?.data?.errors;
            errorEl.textContent = bag ? Object.values(bag).flat().join(' ') : (err?.response?.data?.message ?? cfg.labels.error);
            errorEl.hidden = false;
        }
    });

    const filter = document.querySelector<HTMLInputElement>('[data-settings-filter]');
    filter?.addEventListener('input', () => {
        const q = filter.value.trim().toLowerCase();
        document.querySelectorAll<HTMLElement>('.pl-table tbody tr[data-search]').forEach((tr) => {
            tr.hidden = q !== '' && !(tr.dataset.search || '').includes(q);
        });
    });

    document.querySelector('[data-settings-add]')?.addEventListener('click', () => openModal(null));

    document.querySelectorAll<HTMLElement>('[data-settings-edit]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const row = btn.closest<HTMLElement>('[data-row]');
            openModal(row ? JSON.parse(row.dataset.row || '{}') : null);
        });
    });

    // Themed delete confirmation with a server-computed impact summary (shift types
    // and workplaces); the actual delete soft-deletes server-side.
    const confirmModal = document.querySelector<HTMLElement>('[data-confirm-modal]');
    let pendingId: string | null = null;

    if (confirmModal) {
        const cTitle = confirmModal.querySelector<HTMLElement>('[data-confirm-title]')!;
        const cMsg = confirmModal.querySelector<HTMLElement>('[data-confirm-message]')!;
        const cImpact = confirmModal.querySelector<HTMLElement>('[data-confirm-impact]')!;
        const cNote = confirmModal.querySelector<HTMLElement>('[data-confirm-note]')!;
        const cError = confirmModal.querySelector<HTMLElement>('[data-confirm-error]')!;
        const cOk = confirmModal.querySelector<HTMLButtonElement>('[data-confirm-ok]')!;
        const cCancel = confirmModal.querySelector<HTMLButtonElement>('[data-confirm-cancel]')!;

        const closeConfirm = (): void => { confirmModal.hidden = true; pendingId = null; };
        cCancel.addEventListener('click', closeConfirm);
        confirmModal.addEventListener('click', (e) => { if (e.target === confirmModal) closeConfirm(); });
        document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !confirmModal.hidden) closeConfirm(); }, { signal: screenSignal() });

        cOk.addEventListener('click', async () => {
            if (pendingId === null) {
                return;
            }
            cOk.disabled = true;
            cCancel.disabled = true;
            try {
                await axios().delete(`${cfg.itemBase}/${pendingId}`);
                location.reload();
            } catch (err: any) {
                cOk.disabled = false;
                cCancel.disabled = false;
                cError.textContent = err?.response?.data?.message ?? cfg.labels.error;
                cError.hidden = false;
            }
        });

        const askDelete = async (id: string): Promise<void> => {
            pendingId = id;
            cTitle.textContent = cfg.labels.delete;
            cMsg.textContent = cfg.labels.confirmDelete;
            cOk.textContent = cfg.labels.delete;
            cOk.disabled = false;
            cCancel.disabled = false;
            cImpact.innerHTML = '';
            cImpact.hidden = true;
            cNote.hidden = true;
            cError.hidden = true;
            confirmModal.hidden = false;

            if (cfg.impact) {
                try {
                    const { data } = await axios().get(`${cfg.itemBase}/${id}/delete-impact`);
                    if (Array.isArray(data.lines) && data.lines.length) {
                        cImpact.innerHTML = data.lines.map((l: string) => `<li>${esc(l)}</li>`).join('');
                        cImpact.hidden = false;
                    }
                    if (data.note) {
                        cNote.textContent = data.note;
                        cNote.hidden = false;
                    }
                } catch { /* impact is best-effort — still allow the delete */ }
            }
        };

        document.querySelectorAll<HTMLElement>('[data-settings-delete]').forEach((btn) => {
            btn.addEventListener('click', () => void askDelete(btn.dataset.settingsDelete!));
        });
    }

    // Per-user "sync calendar now": dispatches the push off the request; we just
    // confirm it started (the last-sync tooltip refreshes on the next load).
    document.querySelectorAll<HTMLButtonElement>('[data-sync-user]').forEach((btn) => {
        btn.addEventListener('click', async () => {
            btn.disabled = true;
            try {
                const { data } = await axios().post(btn.dataset.syncUser);
                const summary = typeof data?.imported === 'number'
                    ? `Importováno ${data.imported}, přeskočeno ${data.skipped ?? 0}`
                    : (cfg.labels.syncStarted ?? 'OK');
                toast(summary);
                // Reflect the new sync time in the title without a reload.
                if (data?.at && btn.title.includes(':')) {
                    btn.title = `${btn.title.slice(0, btn.title.lastIndexOf(':'))}: ${data.at}`;
                }
            } catch (err: any) {
                toast(err?.response?.data?.message ?? cfg.labels.error);
            } finally {
                btn.disabled = false;
            }
        });
    });
}
